import * as THREE from "three";
import type { AssetPipeline } from "../core/assets";

// Asset-backed urban core (map01): a street grid built from Kenney's CC0
// City Kit Roads with commercial blocks, a central plaza, parked cars, and
// street trees from the Commercial/Suburban kits. Replaces the primitive
// box-tower core. The whole grid sits inside the terrain's flat center
// (radius 220), so tiles lie flat at y≈0 without conforming.
//
// The escort route's CHARLIE waypoint (100, 80) lands inside block (3,3) —
// that block is authored as an open plaza so the squad has a defensible
// clearing instead of spawning inside a building lot.

type HeightFn = (x: number, z: number) => number;

const GRID_BLOCKS = 4;
const BLOCK_SPAN = 65;
const GRID_HALF = (GRID_BLOCKS * BLOCK_SPAN) / 2; // 130 — grid corner stays inside the flat terrain center (r=220)
const STREET_TILE = 13; // world units per road tile → 5 tiles per block span, streets read narrow vs 52-unit lots

const ROADS = {
  straight: "/models/world/kenney-roads/road-straight.glb",
  crossroad: "/models/world/kenney-roads/road-crossroad.glb",
  square: "/models/world/kenney-roads/road-square.glb",
} as const;

const COMMERCIAL = [
  "/models/world/kenney-commercial/building-a.glb",
  "/models/world/kenney-commercial/building-c.glb",
  "/models/world/kenney-commercial/building-e.glb",
  "/models/world/kenney-commercial/building-g.glb",
  "/models/world/kenney-commercial/building-i.glb",
  "/models/world/kenney-commercial/building-k.glb",
  "/models/world/kenney-commercial/building-m.glb",
] as const;

const SKYSCRAPERS = [
  "/models/world/kenney-commercial/building-skyscraper-a.glb",
  "/models/world/kenney-commercial/building-skyscraper-c.glb",
  "/models/world/kenney-commercial/building-skyscraper-e.glb",
] as const;

const HOUSES = [
  "/models/world/kenney-suburban/building-type-a.glb",
  "/models/world/kenney-suburban/building-type-d.glb",
  "/models/world/kenney-suburban/building-type-g.glb",
  "/models/world/kenney-suburban/building-type-j.glb",
] as const;

const TREES = [
  "/models/world/kenney-suburban/tree-large.glb",
  "/models/world/kenney-suburban/tree-small.glb",
] as const;

const CARS = [
  "/models/world/kenney-car/suv.glb",
  "/models/world/kenney-car/van.glb",
  "/models/world/kenney-car/delivery.glb",
  "/models/world/kenney-car/truck.glb",
] as const;

function seeded(index: number): number {
  const x = Math.sin(index * 127.1) * 43758.5453123;
  return x - Math.floor(x);
}

type TemplateInfo = { template: THREE.Group; size: THREE.Vector3 };

async function loadInfo(assets: AssetPipeline, path: string): Promise<TemplateInfo> {
  const template = await assets.loadSceneTemplate(path);
  const size = new THREE.Box3().setFromObject(template).getSize(new THREE.Vector3());
  return { template, size };
}

function place(
  group: THREE.Group,
  assets: AssetPipeline,
  info: TemplateInfo,
  getHeight: HeightFn,
  x: number,
  z: number,
  rotation: number,
  scale: number,
  flat = false,
  // Kenney's colormap palette is near-white; toned down so the city doesn't
  // blow out under full HDRI daylight. Streets get the strongest cut.
  colorMul = 0.88,
): void {
  const instance = assets.cloneSceneTemplate(info.template);
  const bbox = new THREE.Box3().setFromObject(instance);
  const minY = Number.isFinite(bbox.min.y) ? bbox.min.y : 0;
  instance.position.set(x, getHeight(x, z) - minY * scale + 0.03, z);
  instance.rotation.y = rotation;
  instance.scale.setScalar(scale);
  instance.traverse((obj) => {
    // Matches the established world-dressing contract: imported models are
    // shot-transparent (splash lands under them) but still occlude LOS —
    // except flat street/plaza tiles, which are ground, not cover.
    obj.userData["ignoreBallistics"] = true;
    if (flat) obj.userData["losTransparent"] = true;
    const mesh = obj as THREE.Mesh;
    if (mesh.isMesh) {
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const material of materials) {
        const standard = material as THREE.MeshStandardMaterial;
        if (standard.isMeshStandardMaterial) standard.color.multiplyScalar(colorMul);
      }
    }
  });
  group.add(instance);
}

// Scale so the template's larger horizontal side reaches targetWidth
function scaleFor(info: TemplateInfo, targetWidth: number): number {
  const footprint = Math.max(info.size.x, info.size.z, 0.001);
  return targetWidth / footprint;
}

function addStreetGrid(
  group: THREE.Group,
  assets: AssetPipeline,
  straight: TemplateInfo,
  crossroad: TemplateInfo,
  getHeight: HeightFn,
): void {
  const tileScale = scaleFor(straight, STREET_TILE);
  const crossScale = scaleFor(crossroad, STREET_TILE);
  const lines: number[] = [];
  for (let i = 0; i <= GRID_BLOCKS; i++) lines.push(-GRID_HALF + i * BLOCK_SPAN);

  const isLine = (v: number): boolean => lines.some((line) => Math.abs(line - v) < 0.5);

  for (const line of lines) {
    for (let along = -GRID_HALF + STREET_TILE / 2; along < GRID_HALF; along += STREET_TILE) {
      // East-west street segment (runs along X at z = line)
      if (!isLine(along)) {
        place(group, assets, straight, getHeight, along, line, Math.PI / 2, tileScale, true, 0.72);
        // North-south street segment (runs along Z at x = line)
        place(group, assets, straight, getHeight, line, along, 0, tileScale, true, 0.72);
      }
    }
  }
  for (const lx of lines) {
    for (const lz of lines) {
      place(group, assets, crossroad, getHeight, lx, lz, 0, crossScale, true, 0.72);
    }
  }
}

function addPlaza(
  group: THREE.Group,
  assets: AssetPipeline,
  square: TemplateInfo,
  trees: TemplateInfo[],
  getHeight: HeightFn,
  cx: number,
  cz: number,
): void {
  const tileScale = scaleFor(square, STREET_TILE);
  for (let ix = -1; ix <= 1; ix++) {
    for (let iz = -1; iz <= 1; iz++) {
      place(group, assets, square, getHeight, cx + ix * STREET_TILE, cz + iz * STREET_TILE, 0, tileScale, true, 0.72);
    }
  }
  for (let i = 0; i < 4; i++) {
    const tree = trees[i % trees.length]!;
    const angle = (i / 4) * Math.PI * 2 + 0.4;
    place(
      group, assets, tree, getHeight,
      cx + Math.cos(angle) * 20, cz + Math.sin(angle) * 20,
      seeded(i + 300) * Math.PI * 2, scaleFor(tree, 6),
    );
  }
}

export async function addCityDistrict(group: THREE.Group, assets: AssetPipeline, getHeight: HeightFn): Promise<void> {
  // One flat Promise.all — template loads are the slow part of mission
  // start-up and must overlap, not run in stages.
  const [straight, crossroad, square, commercial, skyscrapers, houses, trees, cars] = await Promise.all([
    loadInfo(assets, ROADS.straight),
    loadInfo(assets, ROADS.crossroad),
    loadInfo(assets, ROADS.square),
    Promise.all(COMMERCIAL.map((p) => loadInfo(assets, p))),
    Promise.all(SKYSCRAPERS.map((p) => loadInfo(assets, p))),
    Promise.all(HOUSES.map((p) => loadInfo(assets, p))),
    Promise.all(TREES.map((p) => loadInfo(assets, p))),
    Promise.all(CARS.map((p) => loadInfo(assets, p))),
  ]);

  const city = new THREE.Group();
  city.name = "city-district";

  addStreetGrid(city, assets, straight, crossroad, getHeight);

  // Blocks: 2×2 building lots each; the lot faces the nearest street.
  let seedIdx = 1;
  for (let gx = 0; gx < GRID_BLOCKS; gx++) {
    for (let gz = 0; gz < GRID_BLOCKS; gz++) {
      const cx = -GRID_HALF + gx * BLOCK_SPAN + BLOCK_SPAN / 2;
      const cz = -GRID_HALF + gz * BLOCK_SPAN + BLOCK_SPAN / 2;

      // The block holding route waypoint CHARLIE stays an open plaza
      if (gx === 3 && gz === 3) {
        addPlaza(city, assets, square, trees, getHeight, cx, cz);
        continue;
      }

      const inner = (gx === 1 || gx === 2) && (gz === 1 || gz === 2);
      for (const [lotX, lotZ] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const) {
        seedIdx++;
        const x = cx + lotX * 13;
        const z = cz + lotZ * 13;
        // Face the nearest street edge of the block
        const rotation = Math.abs(lotX) >= Math.abs(lotZ)
          ? (lotX > 0 ? Math.PI / 2 : -Math.PI / 2)
          : (lotZ > 0 ? 0 : Math.PI);

        if (inner && seeded(seedIdx) > 0.72) {
          const tower = skyscrapers[Math.floor(seeded(seedIdx + 7) * skyscrapers.length)]!;
          place(city, assets, tower, getHeight, x, z, rotation, scaleFor(tower, 15));
        } else if (inner || seeded(seedIdx + 13) > 0.4) {
          const building = commercial[Math.floor(seeded(seedIdx + 3) * commercial.length)]!;
          place(city, assets, building, getHeight, x, z, rotation + (seeded(seedIdx + 5) - 0.5) * 0.06, scaleFor(building, 15 + seeded(seedIdx + 9) * 3));
        } else if (seeded(seedIdx + 17) > 0.25) {
          const house = houses[Math.floor(seeded(seedIdx + 11) * houses.length)]!;
          place(city, assets, house, getHeight, x, z, rotation, scaleFor(house, 11 + seeded(seedIdx + 15) * 2));
        } else {
          const tree = trees[Math.floor(seeded(seedIdx + 19) * trees.length)]!;
          place(city, assets, tree, getHeight, x, z, seeded(seedIdx + 21) * Math.PI * 2, scaleFor(tree, 7));
        }
      }
    }
  }

  // Parked cars hug the street lines just off the through-lanes
  for (let i = 0; i < 18; i++) {
    const car = cars[Math.floor(seeded(i + 500) * cars.length)]!;
    const lineIdx = Math.floor(seeded(i + 520) * (GRID_BLOCKS + 1));
    const line = -GRID_HALF + lineIdx * BLOCK_SPAN;
    const along = (seeded(i + 540) - 0.5) * 2 * (GRID_HALF - 10);
    const side = seeded(i + 560) > 0.5 ? 4.4 : -4.4;
    const northSouth = seeded(i + 580) > 0.5;
    const x = northSouth ? line + side : along;
    const z = northSouth ? along : line + side;
    const rotation = (northSouth ? 0 : Math.PI / 2) + (seeded(i + 600) > 0.5 ? Math.PI : 0);
    place(city, assets, car, getHeight, x, z, rotation, scaleFor(car, 4.6));
  }

  group.add(city);
}
